<template>
  <div class="booking-shell">
    <div class="top-nav-controls">
      <v-btn icon @click="$router.go(-1)" title="Zurück">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
    </div>

    <v-container class="booking-container">
      <div v-if="isLoading" class="text-center mt-16">
        <v-progress-circular indeterminate color="var(--v-primary)" size="64"></v-progress-circular>
      </div>

      <v-card v-else class="booking-card" flat>
        <div v-if="reservationType" class="card-content">
          <div class="header-section text-center">
            <h1 class="headline">{{ reservationType.name }}</h1>
            <p v-if="reservationType.description" class="sub-headline mt-1">{{ reservationType.description }}</p>
          </div>

          <v-window v-model="step">
            <v-window-item :value="1">
              <transition name="slide-fade" mode="out-in">
                <div class="step-wrapper">
                  <h2 class="step-title mb-5 text-center">1. Datum wählen</h2>
                  <v-date-picker
                    v-model="selectedDate"
                    full-width
                    no-title
                    :min="today"
                    :max="maxDateSelect"
                    dark
                    color="var(--v-primary)"
                    class="date-picker-custom"
                    @input="moveToStep(2)"></v-date-picker>
                </div>
              </transition>
            </v-window-item>

            <v-window-item :value="2">
              <transition name="slide-fade" mode="out-in">
                <div class="step-wrapper">
                  <v-btn text @click="moveToStep(1)" class="step-back-btn mb-4"> <v-icon left>mdi-chevron-left</v-icon> Datum ändern </v-btn>
                  <h2 class="step-title text-center mb-5">2. Zeit wählen</h2>

                  <div v-if="userBookingInfo" class="text-center mb-5 booking-limit-info">
                    <v-chip small :color="userBookingInfo.remaining > 0 ? 'green' : 'red'" dark>
                      Noch {{ userBookingInfo.remaining }} von {{ userBookingInfo.limit }} Buchungen in dieser {{ userBookingInfo.unit }} frei
                    </v-chip>
                  </div>

                  <div v-if="isFetchingSlots" class="text-center pa-12">
                    <v-progress-circular indeterminate color="var(--v-primary)"></v-progress-circular>
                  </div>

                  <div v-else-if="timeslots.length && userBookingInfo && userBookingInfo.remaining > 0" class="timeslot-grid">
                    <v-btn
                      v-for="slot in timeslots"
                      :key="slot.time"
                      :disabled="slot.isBooked"
                      @click="selectSlotAndProceed(slot.time)"
                      class="timeslot-btn"
                      depressed>
                      {{ formatTime(slot.time) }}
                    </v-btn>
                  </div>

                  <div v-else-if="userBookingInfo && userBookingInfo.remaining === 0" class="text-center pa-8 no-slots-container">
                    <v-icon class="no-slots-icon" large>mdi-calendar-remove-outline</v-icon>
                    <p class="mt-4">Du hast dein Buchungslimit für diesen Zeitraum erreicht.</p>
                  </div>

                  <div v-else class="text-center pa-8 no-slots-container">
                    <p>Für diesen Tag sind leider keine Termine verfügbar.</p>
                  </div>
                </div>
              </transition>
            </v-window-item>
          </v-window>
        </div>
      </v-card>
    </v-container>

    <v-dialog v-model="bookingDialog" max-width="420px" persistent>
      <v-card dark class="confirm-dialog-card">
        <div class="text-center pa-8 white--text">
          <v-icon class="confirm-icon" color="var(--v-primary)">mdi-calendar-check-outline</v-icon>
          <h2 class="confirm-title mt-4">Buchung bestätigen</h2>
          <p class="confirm-text mt-2">
            Möchtest du den Termin am <br />
            <span class="highlight-text">{{ formatDate(selectedDate) }}</span> um
            <span class="highlight-text">{{ formatTime(selectedTimeslot) }} Uhr</span>
            final buchen?
          </p>
        </div>
        <v-card-actions class="confirm-actions">
          <v-btn text @click="bookingDialog = false" large>Abbrechen</v-btn>
          <v-btn class="confirm-btn white--text" @click="bookTimeslot" large depressed :loading="isBooking">Ja, buchen!</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="errorDialog" max-width="420px" persistent>
      <v-card dark class="confirm-dialog-card">
        <div class="text-center pa-8 white--text">
          <v-icon class="confirm-icon" color="red">mdi-alert-circle-outline</v-icon>
          <h2 class="confirm-title mt-4">Buchung fehlgeschlagen</h2>
          <p class="confirm-text mt-2" v-html="errorMessage"></p>
        </div>
        <v-card-actions class="confirm-actions justify-center">
          <v-btn class="confirm-btn white--text" @click="errorDialog = false" large depressed color="red">Verstanden</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import moment from "moment"
import userService from "@/services/userService"

moment.locale("de")

export default {
  data() {
    return {
      step: 1,
      userService,
      today: new Date().toISOString().substr(0, 10),
      selectedDate: new Date().toISOString().substr(0, 10),
      selectedTimeslot: null,
      timeslots: [],
      bookingDialog: false,
      isBooking: false,
      studioId: null,
      reservationTypeId: this.$route.params.reservationTypeId,
      reservationType: null,
      isLoading: true,
      isFetchingSlots: false,
      userBookingInfo: null, // NEU: Speichert die Info zu den Limits des Nutzers
      errorDialog: false, // NEU: Steuert den Fehlerdialog
      errorMessage: "", // NEU: Nachricht für den Fehlerdialog
    }
  },
  watch: {
    // Lade Timeslots neu, wenn sich das Datum ändert
    selectedDate: "fetchTimeslots",
  },
  async created() {
    console.log("created hook aufgerufen")
    this.studioId = this.userService.getStudio()?._id || null
    await this.fetchReservationTypeDetails()
    await this.fetchTimeslots()
    this.isLoading = false
  },
  methods: {
    moveToStep(stepNumber) {
      this.step = stepNumber
    },
    selectSlotAndProceed(slot) {
      this.selectedTimeslot = slot
      this.bookingDialog = true
    },
    async fetchReservationTypeDetails() {
      try {
        const response = await api.reservationTool.getReservationTypeDetails(this.reservationTypeId)
        this.reservationType = response.data
        this.maxDateSelect = this.$moment().add(14, "days").toISOString().substr(0, 10)
        if (
          this.reservationType.advanceSignupLimit &&
          !isNaN(this.reservationType.advanceSignupLimit) &&
          this.reservationType.advanceSignupLimit !== 0
        ) {
          this.maxDateSelect = this.$moment().add(this.reservationType.advanceSignupLimit, "days").toISOString().substr(0, 10)
        }
      } catch (error) {
        console.error("Fehler beim Laden der Reservierungsdetails:", error)
        // Optional: User-Feedback
      }
    },
    async fetchTimeslots() {
      if (!this.reservationTypeId || !this.selectedDate) return
      this.isFetchingSlots = true
      this.timeslots = []
      this.userBookingInfo = null // Zurücksetzen bei jeder neuen Abfrage
      try {
        const response = await api.reservationTool.getTimeslots(this.reservationTypeId, this.selectedDate)
        // NEU: Speichere BEIDE Informationen aus der Antwort
        this.timeslots = response.data.timeslots
        this.userBookingInfo = response.data.userBookingInfo
      } catch (error) {
        console.error("Fehler beim Laden der Timeslots:", error)
      } finally {
        this.isFetchingSlots = false
      }
    },
    async bookTimeslot() {
      if (!this.selectedTimeslot) return
      this.isBooking = true

      const bookingData = {
        title: this.reservationType.name,
        description: `Buchung für ${this.userService.getFullName()}`,
        studio: this.studioId,
        user: this.userService.getUser()._id,
        startTime: this.selectedTimeslot,
        endTime: moment(this.selectedTimeslot).add(this.reservationType.timeslotDuration, "minutes").toISOString(),
        isBooked: true,
        reservationType: this.reservationTypeId,
      }

      try {
        await api.reservationTool.createReservation(bookingData)
        this.bookingDialog = false // Erfolgreich -> Weiterleitung
        this.$router.push("/reservation") // Ziel anpassen
      } catch (error) {
        console.error("Fehler bei der Buchung:", error)
        this.bookingDialog = false // Bestätigungsdialog schliessen

        // NEU: Spezifische Fehlermeldung vom Backend abfangen
        if (error.response && error.response.status === 409) {
          this.errorMessage = error.response.data
          this.errorDialog = true
        } else {
          // Allgemeine Fehlermeldung
          this.errorMessage = "Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es später erneut."
          this.errorDialog = true
        }
      } finally {
        this.isBooking = false
      }
    },
    formatDate(date) {
      return moment(date).format("dd, DD. MMMM YYYY")
    },
    formatTime(time) {
      return moment(time).format("HH:mm")
    },
  },
}
</script>

<style scoped>
/* NEU: Zusätzliche Styles für die neuen Elemente */
.booking-limit-info .v-chip {
  height: 28px;
  font-weight: 600;
}

.no-slots-container {
  border: 2px dashed var(--border);
  border-radius: 18px;
  color: var(--text-secondary);
}

.no-slots-icon {
  color: var(--text-secondary) !important;
}

.confirm-actions.justify-center {
  justify-content: center;
}
/* --- THEME & TikTok-Style --- */
.booking-shell {
  --primary: #00f2ea; /* Ein leuchtendes Cyan/Türkis */
  --bg: #121212; /* Dunkler Hintergrund */
  --card-bg: #1e1e1e; /* Etwas hellere Karten */
  --text-primary: #ffffff;
  --text-secondary: #a0a0a0; /* Gedämpfteres Grau für sekundären Text */
  --border: #2c2c2c;
  --primary-text: #000000;
  --disabled: #444444;

  background-color: var(--bg);
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: var(--text-primary);
}

/* --- LAYOUT & RESPONSIVE DESIGN --- */
.top-nav-controls {
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 10;
}
.top-nav-controls .v-btn {
  background-color: rgba(0, 0, 0, 0.3);
  color: var(--text-primary) !important;
}
.booking-container {
  padding-top: 70px;
  padding-bottom: 40px;
}
.booking-card {
  background-color: var(--card-bg) !important;
  color: var(--text-primary) !important;
  border-radius: 28px !important;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.2);
  border: 1px solid var(--border);
  max-width: 500px;
  margin: auto;
}
.card-content {
  padding: 24px;
}
@media (max-width: 599px) {
  .card-content {
    padding: 16px;
  }
}

/* --- TYPOGRAPHY --- */
.headline {
  font-size: clamp(1.8rem, 5vw, 2.2rem);
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.2;
}
.sub-headline,
.step-title {
  color: var(--text-secondary);
  font-size: 1.1rem;
}
.step-title {
  font-weight: 600;
  font-size: 1.25rem;
  color: var(--text-primary);
}

/* --- STEP-ANIMATION & STYLING --- */
.step-wrapper {
  padding: 8px 0;
}
.step-back-btn {
  color: var(--text-secondary) !important;
  text-transform: none;
  font-weight: 500;
}
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease-out;
}
.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* --- DATE PICKER --- */
.date-picker-custom {
  border-radius: 18px !important;
  border: 1px solid var(--border);
  background-color: transparent !important;
}
:deep(.v-date-picker-header) {
  background-color: transparent !important;
}
:deep(.v-date-picker-table .v-btn--active) {
  background-color: var(--v-primary) !important;
  color: var(--primary-text) !important;
}

/* --- TIMESLOTS GRID (RESPONSIVE) --- */
.timeslot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
  padding-top: 8px;
}
.timeslot-btn {
  background-color: var(--border) !important;
  color: var(--text-primary) !important;
  border-radius: 12px !important;
  font-weight: 600;
  text-transform: none;
  transition: all 0.2s ease;
  height: 48px !important;
}
.timeslot-btn:hover:not(:disabled) {
  transform: translateY(-3px);
  background-color: var(--v-primary) !important;
  color: var(--primary-text) !important;
}
.timeslot-btn:disabled {
  background-color: var(--disabled) !important;
  color: var(--text-secondary) !important;
  cursor: not-allowed;
  text-decoration: line-through;
}

/* --- CONFIRMATION DIALOG --- */
.confirm-dialog-card {
  border-radius: 24px !important;
  /* background: var(--card-bg) !important;*/
  color: var(--text-primary) !important;
  border: 1px solid var(--border);
}
.confirm-icon {
  font-size: 4rem;
}
.confirm-title {
  font-size: 1.5rem;
  font-weight: 700;
}
.confirm-text {
  color: var(--text-secondary);
  line-height: 1.6;
}
.highlight-text {
  color: var(--v-primary);
  font-weight: 700;
}
.confirm-actions {
  padding: 16px 24px;
}
.confirm-btn {
  background-color: var(--v-primary) !important;
  color: white !important;
  border-radius: 12px !important;
  font-weight: 700;
  text-transform: none;
}
</style>
